import "./tailwind.css"

import * as Sentry from "@sentry/react-router"
import { QueryClientProvider } from "@tanstack/react-query"
import { ConfigProvider, Modal, Skeleton, theme } from "antd"
import Cookies from "js-cookie"
import { useCallback, useEffect, useState } from "react"
import type { LinksFunction, LoaderFunction, MetaFunction } from "react-router"
import {
  ClientLoaderFunction,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useLocation,
  useRouteError
} from "react-router"

import { OSLoader } from "./components/base-loader"
import NotFound from "./components/not-found"
import ProgressBar from "./components/progress-bar"
import RootAgentUI from "./containers/agentic-ui/root-agent-ui"
import RootQueries from "./containers/root-queries"
import { useGetAccounts } from "./hooks/accounts"
import { rootLoader } from "./loaders-actions/root"
import useGlobalStore from "./store/global"
import { ChunkErrorModal } from "./utils/chunk-error-handler"
import { getLinkedinUrl } from "./utils/extension"
import { queryClient } from "./utils/query-client"

export const meta: MetaFunction = () => {
  return [
    { title: "OrbitShift" },
    { name: "description", content: "Welcome to OrbitShift!" }
  ]
}

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous"
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap"
  }
]

export const loader: LoaderFunction = rootLoader

export const clientLoader: ClientLoaderFunction = async ({ serverLoader }) => {
  const data = await serverLoader?.()
  return data ?? null
}

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentUrl }) => {
  return currentUrl.pathname === "/reset-password"
}

export function HydrateFallback({ loaderData }: { loaderData: any }) {
  useGetAccounts()
  const commonTranslations = loaderData?.commonTranslations
  const tenantName = loaderData?.preferences?.show_only_rfp
    ? ""
    : loaderData?.tenant_name
  const isLoggedIn = !!loaderData?.token
  const location = useLocation()
  const showAgenticUI = ["/accounts"].some((url) =>
    location.pathname.includes(url)
  )
  const appTheme = useGlobalStore((store) => store.appTheme)

  return (
    <div className="flex flex-col h-screen">
      {isLoggedIn && (
        <div className="relative flex items-center justify-between w-full px-3 text-13 h-10 border-b-[0.5px] dark:border-gray-200 border-light-border dark:border-b-border dark:bg-dark-1 bg-white">
          <img
            src={
              appTheme === "dark"
                ? "/orbitshift_logo_white.svg"
                : "/orbitshift_logo.svg"
            }
            alt="logo"
            width={140}
            height={33}
            className="mr-5 min-h-[33px]"
          />
          <Skeleton.Avatar active size={28} shape="circle" />
        </div>
      )}
      {!tenantName || !showAgenticUI ? (
        <div className="h-screen flex flex-1 justify-center items-center">
          <OSLoader />
        </div>
      ) : (
        <div className="pt-[20%] flex justify-center">
          <RootAgentUI
            tenantName={tenantName}
            commonTranslations={commonTranslations}
          />
        </div>
      )}
    </div>
  )
}

export function Layout({ children }: { children: React.ReactNode }) {
  const appTheme = useLoaderData()?.appTheme
  const lang = useLoaderData()?.lang || "en"
  const commonTranslations = useLoaderData()?.commonTranslations
  const setAppTheme = useGlobalStore((store) => store.setAppTheme)

  useEffect(() => {
    setAppTheme(appTheme || "light")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <html lang={lang} className={appTheme === "dark" ? "dark" : ""}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="font-sans">
        <ProgressBar />
        {children}
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `
      window.__I18N_STORE__ = ${JSON.stringify(commonTranslations)};
      window.__I18N_LANGUAGE__ = "${lang}";
    `
          }}
        />
        <Scripts />
      </body>
    </html>
  )
}

function App() {
  const appTheme = useGlobalStore((store) => store.appTheme)
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          algorithm:
            appTheme === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
          token: {
            colorPrimary: "#6c5ce7",
            colorLinkActive: "#6c5ce7",
            colorLinkHover: "#6c5ce7",
            colorLink: "#6c5ce7",
            fontFamily: "Inter, sans-serif"
          }
        }}
      >
        <Main />
      </ConfigProvider>
    </QueryClientProvider>
  )
}

export default App

const Main = () => {
  const tokenInfo = useLoaderData()
  const getUserInfo = useGlobalStore((store) => store.getUserInfo)
  const setTokenInfo = useGlobalStore((store) => store.setTokenInfo)
  const setUserLinkedinUrl = useGlobalStore((store) => store.setUserLinkedinUrl)
  const isUserLoaded = useGlobalStore((store) => store.isUserLoaded)
  const setAppTheme = useGlobalStore((store) => store.setAppTheme)
  const setExtentionInstalled = useGlobalStore(
    (store) => store.setExtentionInstalled
  )

  const checkExtensionInstalled = useCallback(() => {
    let retryCount = 0

    const isExtensionInstalled = async () => {
      const isInstalled = !!document.querySelector("orbitshift-ui")

      if (retryCount < 5 && !isInstalled) {
        retryCount++
        const timerId = setTimeout(() => {
          isExtensionInstalled()
          clearTimeout(timerId)
        }, retryCount * 2000)
      }
      if (isInstalled) {
        try {
          const response = await getLinkedinUrl()
          setUserLinkedinUrl(response as string)
        } catch {
          setUserLinkedinUrl("")
        }
      }
      setExtentionInstalled(isInstalled)
      return isInstalled
    }

    isExtensionInstalled()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getUserInfo(tokenInfo)
    setTokenInfo(tokenInfo)
    const theme =
      Cookies.get("app_theme") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light")
    setAppTheme(theme as "light" | "dark")
    document.documentElement.classList.add(theme)
    checkExtensionInstalled()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenInfo])

  if (!isUserLoaded) {
    return <HydrateFallback loaderData={tokenInfo} />
  }

  return (
    <>
      <Outlet />
      <RootQueries />
      <ChunkErrorModal />
    </>
  )
}

export function ErrorBoundary() {
  const appTheme = useGlobalStore((store) => store.appTheme)
  const isDarkMode = appTheme === "dark"
  const [showModal, setShowModal] = useState(false)
  const error = useRouteError()
  if (!isRouteErrorResponse(error) || error.status !== 404) {
    Sentry.captureException(error)
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />
  }

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <div className="relative flex items-center justify-between w-full px-3 text-13 h-12 border-b-[0.5px] dark:border-gray-200 border-light-border dark:border-b-border dark:bg-dark-1 bg-white">
          <a href="/" target="_self">
            <img
              src={
                isDarkMode
                  ? "/orbitshift_logo_white.svg"
                  : "/orbitshift_logo.svg"
              }
              alt="logo"
              width={140}
              height={33}
              className="mr-5 min-h-[33px]"
            />
          </a>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-1 dark:to-dark-2 text-center">
          <div className="max-w-md w-full">
            <div className="mb-8 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500/10 rounded-full animate-ping" />
                <div className="relative bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-full p-8">
                  <svg
                    className="w-20 h-20 text-red-500 dark:text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Oops! Something went wrong
            </h1>

            <p className="text-gray-600 dark:text-gray-300 mb-8 text-base leading-relaxed">
              {isRouteErrorResponse(error)
                ? `We encountered a ${error.status} error`
                : "An unexpected error occurred while processing your request."}
            </p>

            <div className="bg-white dark:bg-dark-3 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  {isRouteErrorResponse(error)
                    ? "This error has been logged and we're working on it"
                    : "Don't worry, our team has been notified"}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => {
                  setShowModal(true)
                  window.location.reload()
                }}
                className="group relative px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2"
              >
                <svg
                  className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>Try Again</span>
              </button>

              <a
                href="/"
                className="px-6 py-3 bg-white dark:bg-dark-3 text-gray-700 dark:text-gray-200 font-medium rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200 flex items-center space-x-2"
                onClick={() => setShowModal(true)}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span>Go Home</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={showModal}
        closable={false}
        footer={null}
        centered
        maskClosable={false}
        keyboard={false}
        zIndex={9999}
        className="chunk-error-modal"
        styles={{
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden"
          },
          mask: {
            backdropFilter: "blur(8px)"
          }
        }}
      >
        <div className="relative overflow-hidden">
          <div
            className={`absolute inset-0 bg-gradient-to-br ${
              isDarkMode ? "from-dark-1 to-dark-2" : "from-gray-50 to-gray-100"
            }`}
          />
          <div
            className={`absolute bottom-0 left-0 w-32 h-32 rounded-full blur-3xl opacity-20 animate-pulse ${
              isDarkMode
                ? "bg-gradient-to-tr from-indigo-500/30 to-blue-500/30"
                : "bg-gradient-to-tr from-indigo-400 to-blue-500"
            }`}
            style={{ animationDelay: "1s" }}
          />
          <div className="relative text-center py-12 px-8 overflow-hidden">
            <div className="mb-8 relative">
              <div
                className={`inline-flex items-center justify-center w-24 h-24 rounded-full shadow-lg ${
                  isDarkMode
                    ? "bg-gradient-to-br from-indigo-900/30 to-purple-900/30"
                    : "bg-gradient-to-br from-indigo-50 to-purple-50"
                }`}
              >
                <div className="relative">
                  <svg
                    className={`w-10 h-10 animate-spin ${
                      isDarkMode ? "text-indigo-400" : "text-indigo-500"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    style={{ animationDuration: "2s" }}
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <div className="absolute inset-0">
                    <div
                      className={`absolute w-2 h-2 rounded-full ${
                        isDarkMode ? "bg-indigo-400" : "bg-indigo-500"
                      }`}
                      style={{
                        top: "0",
                        left: "50%",
                        transform: "translateX(-50%)",
                        animation: "orbit 2s linear infinite"
                      }}
                    ></div>
                  </div>
                </div>
              </div>
              <div
                className={`absolute inset-0 rounded-full animate-ping opacity-20 ${
                  isDarkMode ? "bg-indigo-500/20" : "bg-indigo-500/10"
                }`}
              />
            </div>
          </div>
          <div
            className={`absolute bottom-0 left-0 right-0 h-2 ${
              isDarkMode
                ? "bg-gradient-to-r from-indigo-400 to-purple-500"
                : "bg-gradient-to-r from-indigo-500 to-purple-600"
            }`}
          ></div>
        </div>
      </Modal>
    </>
  )
}
